import styled from '@/styles';

// Centered content column matching the prototype layout (max width 566px on
// the staffing choice screen, wider when rendering the worker table).
export const StepContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '40px 24px 80px',
  boxSizing: 'border-box',
  width: '100%',
});

export const ContentWrap = styled('div', {
  width: '100%',
  maxWidth: 566,
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
  variants: {
    wide: {
      true: { maxWidth: 1100 },
    },
  },
});

// ── Invite sub-screen primitives ──────────────────────────────────────────

export const InviteHeader = styled('h2', {
  fontSize: 18,
  fontWeight: 800,
  color: '#1C1C1C',
  margin: 0,
  fontFamily: "'Manrope', sans-serif",
});

export const ShiftsCard = styled('div', {
  background: '#fff',
  border: '1px solid #DADADA',
  borderRadius: 8,
  padding: 24,
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
});

export const ShiftsHeaderRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
});

export const SelectAllRow = styled('button', {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  cursor: 'pointer',
  background: 'transparent',
  border: 'none',
  padding: 0,
  fontFamily: "'Manrope', sans-serif",
  color: '#1C1C1C',
  fontSize: 16,
});

export const Checkbox = styled('span', {
  width: 24,
  height: 24,
  borderRadius: 4,
  border: '1px solid #979797',
  background: '#fff',
  flexShrink: 0,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  variants: {
    checked: {
      true: {
        background: '#44A735',
        borderColor: '#44A735',
      },
    },
    indeterminate: {
      true: {
        background: '#44A735',
        borderColor: '#44A735',
      },
    },
  },
});

export const SelectionPillBadge = styled('div', {
  background: '#EAF1FF',
  borderRadius: 100,
  padding: '4px 12px',
  fontSize: 16,
  color: '#1C1C1C',
  fontFamily: "'Manrope', sans-serif",
});

export const DateCardsRow = styled('div', {
  display: 'flex',
  gap: 16,
  flexWrap: 'wrap',
});

export const DateCard = styled('div', {
  flex: '1 1 220px',
  minWidth: 220,
  border: '1px solid #DADADA',
  borderRadius: 8,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  background: '#fff',
  fontFamily: "'Manrope', sans-serif",
  variants: {
    selected: {
      true: {
        borderColor: '#A7DAA1',
        background: 'rgba(231,244,230,0.24)',
      },
    },
    // V1 parity (StaffingShiftsCardsSection): a fully-staffed shift card
    // gets the green border. Declared AFTER `selected` so its
    // borderColor wins when a card is both selected and fully staffed,
    // while still keeping the selected background tint.
    fullyStaffed: {
      true: {
        borderColor: '#45A735',
      },
    },
  },
});

export const DateCardHeader = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

export const DateLabel = styled('p', {
  fontSize: 16,
  fontWeight: 700,
  color: '#1C1C1C',
  margin: 0,
});

export const DowRow = styled('div', {
  display: 'flex',
  gap: 4,
});

export const DowDot = styled('span', {
  width: 20,
  height: 20,
  borderRadius: '50%',
  border: '1px solid #DADADA',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 11,
  fontWeight: 600,
  color: '#979797',
  variants: {
    active: {
      true: {
        borderColor: '#44A735',
        color: '#2E8722',
      },
    },
  },
});

export const TimeText = styled('p', {
  fontSize: 14,
  fontWeight: 600,
  color: '#1C1C1C',
  margin: 0,
});

export const BreakText = styled('p', {
  fontSize: 13,
  fontWeight: 400,
  color: '#5B5B5B',
  margin: 0,
});

export const InvitedRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  borderTop: '1px solid rgba(167,218,161,0.6)',
  paddingTop: 12,
  fontSize: 14,
  color: '#1C1C1C',
});

export const AvatarPlaceholder = styled('span', {
  width: 24,
  height: 24,
  borderRadius: '50%',
  background: '#F0F0F0',
  border: '1px solid #DADADA',
  display: 'inline-block',
});

// "+N" overflow bubble shown after the first 3 avatars on the per-shift
// Invited row. Same size as the avatar bubbles so the row stays
// visually aligned — distinguished by a neutral grey fill.
export const AvatarOverflowBubble = styled('span', {
  width: 24,
  height: 24,
  borderRadius: '50%',
  background: '#EAEAEA',
  border: '1px solid #DADADA',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#1C1C1C',
  fontSize: 10,
  fontWeight: 700,
  fontFamily: "'Manrope', sans-serif",
});

// Mini avatar used on the per-shift "Invited" row inside DateCard.
// Falls back to a single-initial circle when no avatarUrl is present,
// matching the worker-list cell's avatar treatment.
export const InviteAvatar = styled('span', {
  width: 24,
  height: 24,
  borderRadius: '50%',
  background: '#E7F3E6',
  border: '1px solid #DADADA',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  color: '#1C1C1C',
  fontSize: 11,
  fontWeight: 700,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '50%',
  },
});

export const StepTitle = styled('h2', {
  fontSize: 18,
  fontWeight: 800,
  color: '#1C1C1C',
  margin: 0,
  fontFamily: "'Manrope', sans-serif",
});

export const ChoiceSection = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
});

// Rendered as a div instead of a button so we can nest the inner
// ToggleSwitch (also a button) without violating DOM nesting rules.
// Keyboard activation is wired up at the call site via onKeyDown.
export const ChoiceCard = styled('div', {
  width: '100%',
  background: '#fff',
  border: '1px solid #DADADA',
  borderRadius: 8,
  padding: '20px 24px',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 24,
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: "'Manrope', sans-serif",
  transition: 'border-color 0.15s, box-shadow 0.15s',
  boxSizing: 'border-box',
  '&:hover': {
    borderColor: '#44A735',
    boxShadow: '0 0 0 3px rgba(68,167,53,0.10)',
  },
  '&:focus-visible': {
    outline: '2px solid #44A735',
    outlineOffset: 2,
  },
  variants: {
    selected: {
      true: {
        borderColor: '#44A735',
        boxShadow: '0 0 0 3px rgba(68,167,53,0.10)',
      },
    },
  },
});

export const ChoiceIcon = styled('div', {
  width: 60,
  height: 60,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const ChoiceBody = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  flex: 1,
  minWidth: 0,
});

export const ChoiceTitle = styled('p', {
  fontSize: 16,
  fontWeight: 700,
  color: '#1C1C1C',
  margin: 0,
  fontFamily: "'Manrope', sans-serif",
});

export const ChoiceDescription = styled('p', {
  fontSize: 16,
  fontWeight: 400,
  color: '#5B5B5B',
  margin: 0,
  fontFamily: "'Manrope', sans-serif",
});

export const ChoiceRadio = styled('div', {
  width: 24,
  height: 24,
  borderRadius: '50%',
  border: '1px solid #979797',
  background: '#fff',
  flexShrink: 0,
  marginTop: 2,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  variants: {
    selected: {
      true: {
        borderColor: '#44A735',
        '&::after': {
          content: '""',
          width: 12,
          height: 12,
          borderRadius: '50%',
          background: '#44A735',
        },
      },
    },
  },
});

export const AutoFillCard = styled('div', {
  background: '#fff',
  border: '1px solid #DADADA',
  borderRadius: 8,
  padding: '12px 16px',
  display: 'flex',
  alignItems: 'center',
  gap: 16,
});

export const AutoFillText = styled('p', {
  fontSize: 14,
  fontWeight: 400,
  color: '#5B5B5B',
  margin: 0,
  fontFamily: "'Manrope', sans-serif",
  flex: 1,
});

export const ToggleSwitch = styled('button', {
  position: 'relative',
  width: 44,
  height: 24,
  background: '#B7B7B7',
  border: 'none',
  borderRadius: 100,
  cursor: 'pointer',
  flexShrink: 0,
  transition: 'background 0.2s',
  padding: 0,
  variants: {
    on: {
      true: { background: '#44A735' },
    },
  },
});

export const ToggleKnob = styled('span', {
  position: 'absolute',
  top: 3,
  left: 3,
  width: 18,
  height: 18,
  background: '#fff',
  borderRadius: '50%',
  transition: 'all 0.2s',
  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  variants: {
    on: {
      true: { left: 'auto', right: 3 },
    },
  },
});

// ── Worker list panel (Send Invitations selection) ────────────────────────

export const WorkerPanel = styled('div', {
  background: '#fff',
  border: '1px solid #DADADA',
  borderRadius: 8,
  overflow: 'hidden',
});

export const PanelTitleRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 24px 8px',
  gap: 16,
});

export const PanelTitle = styled('p', {
  fontSize: 18,
  fontWeight: 800,
  color: '#1C1C1C',
  margin: 0,
  fontFamily: "'Manrope', sans-serif",
});

export const SearchWrap = styled('div', {
  position: 'relative',
});

export const SearchIconAbs = styled('span', {
  position: 'absolute',
  left: 10,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 16,
  height: 16,
  pointerEvents: 'none',
  display: 'flex',
});

export const SearchInput = styled('input', {
  width: 320,
  border: '1px solid #DADADA',
  borderRadius: 8,
  padding: '8px 12px 8px 34px',
  fontSize: 14,
  color: '#1C1C1C',
  fontFamily: "'Manrope', sans-serif",
  outline: 'none',
  boxSizing: 'border-box',
});

export const TabsRow = styled('div', {
  display: 'flex',
  borderBottom: '1px solid #DADADA',
  padding: '0 16px',
});

export const TabButton = styled('button', {
  fontSize: 14,
  fontFamily: "'Manrope', sans-serif",
  color: '#1C1C1C',
  fontWeight: 400,
  background: 'transparent',
  border: 'none',
  borderBottom: '2px solid transparent',
  padding: '8px 16px',
  cursor: 'pointer',
  variants: {
    active: {
      true: {
        color: '#2E8722',
        fontWeight: 700,
        borderBottomColor: '#44A735',
      },
    },
  },
});

// Column layout mirrors v1's `TABLE_HEADERS`:
//   Worker details (avatar + name + rating/favorite) | {Skill} Shifts |
//   Shifts With You | Hours This Week | Action (Invite + Hire side-by-side,
//   no column label).
// Hours This Week is Tenant-Admin-only (GRAVY-10073), so both row grids
// carry a `withoutHours` variant that drops its 140px track.
const STAFFING_COLUMNS = '1fr 140px 140px 140px 240px';
const STAFFING_COLUMNS_WITHOUT_HOURS = '1fr 140px 140px 240px';

export const HeaderRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: STAFFING_COLUMNS,
  padding: '8px 24px',
  borderBottom: '1px solid #F5F5F5',
  fontSize: 12,
  fontWeight: 600,
  color: '#5B5B5B',
  fontFamily: "'Manrope', sans-serif",

  variants: {
    withoutHours: {
      true: { gridTemplateColumns: STAFFING_COLUMNS_WITHOUT_HOURS },
    },
  },
});

export const WorkerRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: STAFFING_COLUMNS,
  alignItems: 'center',
  gap: 12,
  padding: '12px 24px',
  borderBottom: '1px solid #F5F5F5',
  fontFamily: "'Manrope', sans-serif",
  '&:last-child': { borderBottom: 'none' },

  variants: {
    withoutHours: {
      true: { gridTemplateColumns: STAFFING_COLUMNS_WITHOUT_HOURS },
    },
  },
});

export const WorkerCell = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 12,
  minWidth: 0,
});

export const WorkerAvatar = styled('div', {
  width: 48,
  height: 48,
  borderRadius: '50%',
  background: '#EBEBEB',
  color: '#5B5B5B',
  fontSize: 16,
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  flex: '0 0 auto',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
});

export const WorkerTextStack = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 0,
});

export const WorkerName = styled('div', {
  fontSize: 14,
  fontWeight: 600,
  color: '#1C1C1C',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const WorkerMetaRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  color: '#5B5B5B',
});

// Single-stat cell rendered in its own column (e.g. "3 Server Shifts").
// `count` sits in front of a muted label so the design matches the v1
// `<b>{count}</b> {label}` pattern.
export const ShiftStatCell = styled('div', {
  fontSize: 13,
  color: '#5B5B5B',
  '& strong': {
    color: '#1C1C1C',
    fontWeight: 700,
    marginRight: 4,
  },
});

export const ActionButtonsCell = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
});

export const ConflictBadge = styled('span', {
  display: 'inline-block',
  marginLeft: 8,
  background: '#FCE7E7',
  color: '#A0292B',
  borderRadius: 6,
  padding: '2px 6px',
  fontSize: 11,
  fontWeight: 600,
});

// Mirrors v1's `buttonStyles`:
//   add      → green outline (#45A735), green text
//   remove   → dark fill (#262626), white text (selected)
//   disabled → grey outline (#A1A1A1), grey text
export const SelectionPill = styled('button', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  border: '1px solid #45A735',
  borderRadius: 999,
  padding: '6px 14px',
  background: '#fff',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: "'Manrope', sans-serif",
  color: '#45A735',
  whiteSpace: 'nowrap',
  variants: {
    state: {
      default: {},
      invited: {
        background: '#262626',
        borderColor: '#262626',
        color: '#fff',
      },
      hired: {
        background: '#262626',
        borderColor: '#262626',
        color: '#fff',
      },
      disabled: {
        borderColor: '#A1A1A1',
        color: '#A1A1A1',
        cursor: 'not-allowed',
      },
    },
  },
});

export const PaginationRow = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 24px',
  borderTop: '1px solid #F5F5F5',
  fontFamily: "'Manrope', sans-serif",
});

// ── Certificate (badge) picker on the choice screen ─────────────────────
// Originally a bordered card with a small label; redesign drops the
// border and uses a section heading + a select-like dropdown for the
// Badge Requirements control.
export const CertificateCard = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  fontFamily: "'Manrope', sans-serif",
});

export const CertificateTitle = styled('p', {
  fontSize: 18,
  fontWeight: 700,
  color: '#1C1C1C',
  margin: 0,
});

export const CertificateHint = styled('p', {
  fontSize: 12,
  color: '#5B5B5B',
  margin: 0,
});

export const CertificateChipsRow = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
});

export const CertificateChip = styled('button', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  border: '1px solid #DADADA',
  borderRadius: 999,
  padding: '6px 12px',
  background: '#fff',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  color: '#1C1C1C',
  fontFamily: "'Manrope', sans-serif",
  variants: {
    selected: {
      true: {
        background: 'rgba(68,167,53,0.10)',
        borderColor: '#44A735',
        color: '#2E8722',
      },
    },
  },
});

export const CertificateChipImage = styled('img', {
  width: 16,
  height: 16,
  objectFit: 'contain',
});

// ── Badge Requirements dropdown (Tenant-admin redesign) ─────────────────
// Replaces the older inline chip list. The trigger reads as a standard
// form select; the popover below it surfaces a checkbox per badge.
export const BadgeDropdownWrap = styled('div', {
  position: 'relative',
  fontFamily: "'Manrope', sans-serif",
});

export const BadgeDropdownTrigger = styled('button', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  height: 44,
  padding: '0 16px',
  background: '#fff',
  border: '1px solid #DADADA',
  borderRadius: 8,
  fontFamily: "'Manrope', sans-serif",
  fontSize: 14,
  color: '#1C1C1C',
  cursor: 'pointer',
  textAlign: 'left',
});

export const BadgeDropdownTriggerLabel = styled('span', {
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: '#979797',
  variants: {
    hasSelection: {
      true: { color: '#1C1C1C', fontWeight: 600 },
    },
  },
});

export const BadgeDropdownPopover = styled('div', {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  background: '#fff',
  border: '1px solid #DADADA',
  borderRadius: 8,
  boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
  zIndex: 50,
  overflow: 'hidden',
  maxHeight: 280,
  overflowY: 'auto',
});

export const BadgeOptionRow = styled('button', {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  padding: '12px 16px',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontFamily: "'Manrope', sans-serif",
  fontSize: 14,
  color: '#1C1C1C',
  textAlign: 'left',
  '&:hover': { background: '#F5F5F5' },
  '&:not(:last-of-type)': { borderBottom: '1px solid #F2F2F2' },
});

export const PageButton = styled('button', {
  background: '#fff',
  border: '1px solid #44A735',
  color: '#2E8722',
  borderRadius: 8,
  height: 32,
  padding: '0 16px',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: "'Manrope', sans-serif",
  cursor: 'pointer',
  '&:disabled': {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
});
